import * as THREE from 'three';
import { seaSurface, surfaceColumnFromSample, createSurfaceColumn, type TerrainSample } from '@hyperlight/shared';
import type { Planet } from '../planet';

export const MIST_GRID = 24, MIST_CELL = 64, MIST_BUDGET = 24;
const smooth = THREE.MathUtils.smoothstep;
interface Cell { height:number; moisture:number; water:number; time:number; confidence:number }

/** Damp depressions retain mist; exposed crests do not. A flat wet lowland
 * keeps a weaker layer. Heights are radial metres, never viewer altitude. */
export function mistHabitat(height:number, surrounding:number, moisture:number, water:number):number {
  const wet = Math.max(smooth(moisture,-.25,.65),water);
  const hollow = smooth(surrounding-height,2,24);
  const exposed = smooth(height-surrounding,3,22);
  return wet * Math.max(water*.85,(.24+.76*hollow)*(1-.92*exposed));
}

/** Coarse regional support for mist, separate from the 4 m roof/drop cache.
 * One 9 KiB texture, bounded sampler work, and retained cells while walking.
 * Unknown cells fade in after their cardinal neighbours become available. */
export class TerrainMistField {
  private readonly pixels = new Float32Array(MIST_GRID*MIST_GRID*4);
  readonly texture = new THREE.DataTexture(this.pixels,MIST_GRID,MIST_GRID,THREE.RGBAFormat,THREE.FloatType);
  readonly uniforms = {
    uMistTerrain:{value:this.texture as THREE.Texture},
    uMistAnchor:{value:new THREE.Vector3()}, uMistA:{value:new THREE.Vector3()}, uMistB:{value:new THREE.Vector3()},
    uMistGrid:{value:new THREE.Vector2()},
  };
  private planet:Planet|null=null;
  private readonly cells=new Map<string,Cell>();
  private readonly point=new THREE.Vector3();
  private readonly up=new THREE.Vector3();
  private readonly sample={} as TerrainSample;
  private readonly surface=createSurfaceColumn();
  private cursor=0;
  private readyCells=0;
  private lastSamples=0;
  private settled=false;

  constructor(){this.texture.minFilter=this.texture.magFilter=THREE.NearestFilter;this.texture.generateMipmaps=false;}

  update(planet:Planet,local:THREE.Vector3,time:number):void {
    const u=this.uniforms;
    // Moving radially (flight) must not drag the stored terrain datum upwards.
    this.point.copy(local).normalize().multiplyScalar(planet.def.radius);
    if(this.planet!==planet||this.point.distanceToSquared(u.uMistAnchor.value)>2048*2048){
      this.planet=planet;this.cells.clear();this.cursor=0;
      this.settled=false;
      u.uMistAnchor.value.copy(this.point);this.up.copy(this.point).normalize();
      u.uMistA.value.set(Math.abs(this.up.x)>.9?0:1,Math.abs(this.up.x)>.9?1:0,0).cross(this.up).normalize();
      u.uMistB.value.crossVectors(this.up,u.uMistA.value).normalize();
    }
    this.point.copy(local).multiplyScalar(planet.def.radius*planet.def.radius/local.dot(u.uMistAnchor.value)).sub(u.uMistAnchor.value);
    const gx=Math.floor(this.point.dot(u.uMistA.value)/MIST_CELL)-MIST_GRID/2;
    const gy=Math.floor(this.point.dot(u.uMistB.value)/MIST_CELL)-MIST_GRID/2;
    const moved=u.uMistGrid.value.x!==gx||u.uMistGrid.value.y!==gy;
    u.uMistGrid.value.set(gx,gy);this.lastSamples=0;
    // Scan all candidates, but pay for at most MIST_BUDGET fresh terrain samples.
    // Keep a one-cell halo so every visible cell has a neighbourhood.
    const span=MIST_GRID+2;
    for(let visit=0;visit<span*span&&this.lastSamples<MIST_BUDGET;visit++){
      const index=this.cursor++%(span*span);
      const x=(index%span+span/2)%span+gx-1,y=(Math.floor(index/span)+span/2)%span+gy-1;
      const key=`${x},${y}`,old=this.cells.get(key);
      if(old&&time>=old.time&&time-old.time<12)continue;
      this.point.copy(u.uMistAnchor.value).addScaledVector(u.uMistA.value,(x+.5)*MIST_CELL).addScaledVector(u.uMistB.value,(y+.5)*MIST_CELL).normalize();
      const s=planet.sampler.sample(this.point.x,this.point.y,this.point.z,this.sample,true);
      const height=s.h*planet.sampler.amplitude;
      const waterHeight=Math.max(planet.def.seaRadius===null?-1e9:planet.def.seaRadius-planet.def.radius,(s.riverWs??-1e9)*planet.sampler.amplitude,s.wetlandWaterM??-1e9);
      const water=waterHeight>height-.5?1:0;
      const special=seaSurface(planet.def)!=='water'||planet.def.archetype==='volcanic';
      if(special)surfaceColumnFromSample(planet.def,planet.sampler.amplitude,s,this.surface);
      this.cells.set(key,{height:special?this.surface.topR-planet.def.radius:Math.max(height,waterHeight),
        moisture:s.moist,water:special?(this.surface.liquid==='water'?1:0):water,time,confidence:old?.confidence??0});this.lastSamples++;
    }
    // Wind/clouds keep ticking every frame. This terrain support texture only
    // changes on movement, refresh, or while confidence is still fading in.
    if(!moved&&this.lastSamples===0&&this.settled)return;
    this.readyCells=0;this.settled=true;
    for(let y=0;y<MIST_GRID;y++)for(let x=0;x<MIST_GRID;x++){
      const cx=x+gx,cy=y+gy,c=this.cells.get(`${cx},${cy}`),i=(y*MIST_GRID+x)*4;
      const left=this.cells.get(`${cx-1},${cy}`),right=this.cells.get(`${cx+1},${cy}`),
        below=this.cells.get(`${cx},${cy-1}`),above=this.cells.get(`${cx},${cy+1}`);
      if(!c||!left||!right||!below||!above){this.pixels.fill(0,i,i+4);this.settled=false;continue;}
      const surrounding=(left.height+right.height+below.height+above.height)/4;
      this.pixels[i]=c.height;
      this.pixels[i+1]=mistHabitat(c.height,surrounding,c.moisture,c.water);
      this.pixels[i+2]=c.moisture;
      c.confidence=Math.min(1,c.confidence+.08);
      if(c.confidence<1)this.settled=false;
      this.pixels[i+3]=c.confidence;this.readyCells++;
    }
    if(this.cells.size>span*span*3)for(const key of this.cells.keys()){
      const [x,y]=key.split(',').map(Number);
      if(x<gx-1||x>gx+MIST_GRID||y<gy-1||y>gy+MIST_GRID)this.cells.delete(key);
    }
    this.texture.needsUpdate=true;
  }
  stats(){return {ready:this.readyCells,total:MIST_GRID*MIST_GRID,cached:this.cells.size,samples:this.lastSamples,bytes:this.pixels.byteLength};}
  dispose(){this.texture.dispose();this.cells.clear();this.planet=null;}
}

export const TERRAIN_MIST_GLSL=/* glsl */`
uniform sampler2D uMistTerrain;
uniform vec3 uMistAnchor,uMistA,uMistB;
uniform vec2 uMistGrid;
// Manual bilinear interpolation works without float-linear texture support.
// Validity is interpolated separately so unknown cells cannot pull heights to 0.
vec3 mistTerrain(vec3 p){
  vec3 d=p*(uWxRadius*uWxRadius/max(1.0,dot(p,uMistAnchor)))-uMistAnchor;
  vec2 grid=vec2(dot(d,uMistA),dot(d,uMistB))/${MIST_CELL.toFixed(1)}-uMistGrid-.5;
  vec2 base=floor(grid),f=fract(grid);vec2 sum=vec2(0.0);float valid=0.0;
  for(int y=0;y<=1;y++)for(int x=0;x<=1;x++){
    vec2 cell=base+vec2(float(x),float(y)),uv=(cell+.5)/${MIST_GRID.toFixed(1)};
    vec4 v=texture2D(uMistTerrain,uv);
    float inside=step(0.0,min(cell.x,cell.y))*step(max(cell.x,cell.y),${(MIST_GRID-1).toFixed(1)});
    float w=(x==0?1.0-f.x:f.x)*(y==0?1.0-f.y:f.y)*v.a*inside;
    sum+=v.rg*w;valid+=w;
  }
  vec2 uv=(grid+.5)/${MIST_GRID.toFixed(1)};
  float edge=smoothstep(0.0,.12,min(min(uv.x,uv.y),min(1.0-uv.x,1.0-uv.y)));
  return vec3(sum/max(valid,.00001),valid*edge);
}
`;
